import { useState, useEffect, useCallback, useMemo } from 'react';
import { ClipboardList, Download, RefreshCw, Search, X, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { apiClient } from '../lib/apiClient';
import { AUDIT_ACTIONS, AUDIT_ENTITIES } from '../lib/auditLog';
import { DataTable, type ColumnDef } from './DataTable';

interface AuditLogEntry {
  id: string;
  user_email: string;
  user_role: string;
  action: string;
  entity_type: string;
  entity_id: string | null;
  entity_label: string | null;
  parent_entity_type: string | null;
  parent_entity_id: string | null;
  parent_entity_label: string | null;
  details: Record<string, unknown> | null;
  page_context: string | null;
  created_at: string;
}

interface ListEnvelope {
  rows: AuditLogEntry[];
  total: number;
}

interface SummaryStats {
  today: number;
  this_week: number;
  active_users: number;
}

const ACTION_LABELS: Record<string, string> = {
  create: 'Created',
  update: 'Updated',
  delete: 'Deleted',
  submit: 'Submitted',
  approve: 'Approved',
  reject: 'Rejected',
  upload: 'Uploaded',
  email_sent: 'Email Sent',
  login: 'Login',
  logout: 'Logout',
};

const ACTION_COLORS: Record<string, string> = {
  create: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300',
  update: 'bg-blue-100 text-blue-800 dark:bg-blue-500/20 dark:text-blue-300',
  delete: 'bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-300',
  submit: 'bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300',
  approve: 'bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-300',
  reject: 'bg-orange-100 text-orange-800 dark:bg-orange-500/20 dark:text-orange-300',
  upload: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-500/20 dark:text-cyan-300',
  email_sent: 'bg-sky-100 text-sky-800 dark:bg-sky-500/20 dark:text-sky-300',
  login: 'bg-teal-100 text-teal-800 dark:bg-teal-500/20 dark:text-teal-300',
  logout: 'bg-subtle text-theme-secondary',
};

const ENTITY_LABELS: Record<string, string> = {
  session: 'Session',
  inspection_report: 'Inspection Report',
  property: 'Property',
  controller: 'Controller',
  zone: 'Zone',
  double_check: 'Double Check',
  water_meter: 'Water Meter',
  user: 'User',
  work_code: 'Work Code',
  zone_type: 'Zone Type',
  manufacturer: 'Manufacturer',
  report_status: 'Report Status',
  brand_settings: 'Brand Settings',
  report_style: 'Report Style',
  integration: 'Integration',
  feedback: 'Feedback',
  photo: 'Photo',
};

const DATE_PRESETS = [
  { label: 'Last Hour', hours: 1 },
  { label: 'Last 24h', hours: 24 },
  { label: 'Last 3 Days', hours: 72 },
  { label: 'Last 7 Days', hours: 168 },
];

const AUTO_REFRESH_STORAGE_KEY = 'audit_log_auto_refresh_ms';
const AUTO_REFRESH_OPTIONS: { label: string; value: number }[] = [
  { label: 'Off',      value: 0 },
  { label: '5 sec',    value: 5_000 },
  { label: '10 sec',   value: 10_000 },
  { label: '30 sec',   value: 30_000 },
  { label: '1 min',    value: 60_000 },
  { label: '5 min',    value: 300_000 },
];
const AUTO_REFRESH_VALUES = new Set(AUTO_REFRESH_OPTIONS.map((o) => o.value));
const DEFAULT_AUTO_REFRESH_MS = 5_000;

function getDateRange(hours: number): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to.getTime() - hours * 60 * 60 * 1000);
  return { from: from.toISOString(), to: to.toISOString() };
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export function AuditLogViewer() {
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [sortKey, setSortKey] = useState('created_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const [datePreset, setDatePreset] = useState(24);
  const [filterAction, setFilterAction] = useState('');
  const [filterEntity, setFilterEntity] = useState('');
  const [filterUser, setFilterUser] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [stats, setStats] = useState<SummaryStats>({ today: 0, this_week: 0, active_users: 0 });
  const [exporting, setExporting] = useState(false);
  const [users, setUsers] = useState<string[]>([]);

  // Auto-refresh interval (in ms; 0 = disabled). Default is 5 seconds, but the
  // user can pick another preset or turn it off entirely. Choice persists in
  // localStorage so it survives navigation and refreshes.
  const [autoRefreshMs, setAutoRefreshMs] = useState<number>(() => {
    if (typeof localStorage === 'undefined') return DEFAULT_AUTO_REFRESH_MS;
    const raw = localStorage.getItem(AUTO_REFRESH_STORAGE_KEY);
    if (raw === null) return DEFAULT_AUTO_REFRESH_MS;
    const n = Number(raw);
    return AUTO_REFRESH_VALUES.has(n) ? n : DEFAULT_AUTO_REFRESH_MS;
  });
  // Tracks whether a silent auto-refresh tick is in flight, so the UI can
  // surface a subtle indicator without ever flipping the main `loading` flag
  // (which would flash the spinner over the whole table every interval).
  const [autoRefreshing, setAutoRefreshing] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(AUTO_REFRESH_STORAGE_KEY, String(autoRefreshMs));
    } catch {
      /* localStorage disabled — best-effort only */
    }
  }, [autoRefreshMs]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setPage(0);
  }, [datePreset, filterAction, filterEntity, filterUser, debouncedSearch, pageSize]);

  const buildParams = useCallback((override: Record<string, string> = {}) => {
    const { from, to } = getDateRange(datePreset);
    const params = new URLSearchParams();
    params.set('from', from);
    params.set('to', to);
    if (filterAction) params.set('action', filterAction);
    if (filterEntity) params.set('entity_type', filterEntity);
    if (filterUser) params.set('user_email', filterUser);
    if (debouncedSearch) params.set('search', debouncedSearch);
    params.set('sort_by', sortKey);
    params.set('sort_dir', sortDir);
    params.set('limit', String(pageSize));
    params.set('offset', String(page * pageSize));
    for (const [k, v] of Object.entries(override)) params.set(k, v);
    return params;
  }, [datePreset, filterAction, filterEntity, filterUser, debouncedSearch, sortKey, sortDir, pageSize, page]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const result = await apiClient.get<ListEnvelope>(`/api/audit-logs?${buildParams().toString()}`);
      setLogs(result.rows);
      setTotalCount(result.total);
    } catch (err) {
      console.error('Error fetching audit logs:', err);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  const fetchStats = useCallback(async () => {
    try {
      const data = await apiClient.get<SummaryStats>('/api/audit-logs/stats');
      setStats(data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const data = await apiClient.get<string[]>('/api/audit-logs/users');
      setUsers(data);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  }, []);

  useEffect(() => {
    void fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    void fetchStats();
    void fetchUsers();
  }, [fetchStats, fetchUsers]);

  // Silent refresh: refetches logs + stats without touching the main `loading`
  // flag, so the table doesn't flash a spinner on every auto-refresh tick.
  const refreshSilently = useCallback(async () => {
    setAutoRefreshing(true);
    try {
      const [logsResult, statsResult] = await Promise.all([
        apiClient.get<ListEnvelope>(`/api/audit-logs?${buildParams().toString()}`),
        apiClient.get<SummaryStats>('/api/audit-logs/stats'),
      ]);
      setLogs(logsResult.rows);
      setTotalCount(logsResult.total);
      setStats(statsResult);
    } catch (err) {
      // Auto-refresh failures are non-fatal — next tick will retry. Don't
      // surface a notify(); a flaky network during a 5s tick is normal.
      console.error('Auto-refresh failed:', err);
    } finally {
      setAutoRefreshing(false);
    }
  }, [buildParams]);

  // Drive the auto-refresh interval. Pauses while the tab is hidden so we
  // aren't hammering Postgres for an Activity Log nobody is looking at.
  useEffect(() => {
    if (autoRefreshMs <= 0) return;
    const tick = () => {
      if (typeof document === 'undefined' || document.visibilityState === 'visible') {
        void refreshSilently();
      }
    };
    const id = window.setInterval(tick, autoRefreshMs);
    return () => window.clearInterval(id);
  }, [autoRefreshMs, refreshSilently]);

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = buildParams({ limit: '10000', offset: '0' });
      const result = await apiClient.get<ListEnvelope>(`/api/audit-logs?${params.toString()}`);
      const rows = result.rows.map((row) => ({
        Timestamp: new Date(row.created_at).toISOString(),
        User: row.user_email,
        Role: row.user_role,
        Action: ACTION_LABELS[row.action] || row.action,
        'Entity Type': ENTITY_LABELS[row.entity_type] || row.entity_type,
        'Entity Label': row.entity_label || '',
        'Parent Type': row.parent_entity_type ? (ENTITY_LABELS[row.parent_entity_type] || row.parent_entity_type) : '',
        'Parent Label': row.parent_entity_label || '',
        Details: row.details ? JSON.stringify(row.details) : '',
        Page: row.page_context || '',
      }));

      const headers = Object.keys(rows[0] || {});
      const csvContent = [
        headers.join(','),
        ...rows.map((row) =>
          headers.map((h) => {
            const val = String((row as Record<string, string>)[h] || '');
            return val.includes(',') || val.includes('"') || val.includes('\n')
              ? `"${val.replace(/"/g, '""')}"`
              : val;
          }).join(','),
        ),
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export error:', err);
    } finally {
      setExporting(false);
    }
  };

  const clearFilters = () => {
    setDatePreset(24);
    setFilterAction('');
    setFilterEntity('');
    setFilterUser('');
    setSearchQuery('');
    setDebouncedSearch('');
  };

  const hasActiveFilters = filterAction || filterEntity || filterUser || searchQuery;

  const columns: ColumnDef<AuditLogEntry>[] = useMemo(() => [
    {
      key: 'created_at',
      label: 'Time',
      sortable: true,
      headerClassName: 'w-40',
      render: (entry) => (
        <span className="text-xs text-theme-secondary font-mono whitespace-nowrap">
          {formatTimestamp(entry.created_at)}
        </span>
      ),
    },
    {
      key: 'user_email',
      label: 'User',
      sortable: true,
      render: (entry) => (
        <div className="min-w-0">
          <p className="text-sm text-theme-primary truncate">{entry.user_email || 'System'}</p>
          <p className="text-xs text-theme-secondary">{entry.user_role}</p>
        </div>
      ),
    },
    {
      key: 'action',
      label: 'Action',
      sortable: true,
      headerClassName: 'w-28',
      render: (entry) => (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${ACTION_COLORS[entry.action] || 'bg-subtle text-theme-secondary'}`}>
          {ACTION_LABELS[entry.action] || entry.action}
        </span>
      ),
    },
    {
      key: 'entity_type',
      label: 'Entity',
      sortable: true,
      render: (entry) => (
        <div className="min-w-0">
          <p className="text-sm text-theme-secondary">{ENTITY_LABELS[entry.entity_type] || entry.entity_type}</p>
          {entry.entity_label && (
            <p className="text-xs text-theme-secondary truncate max-w-48">{entry.entity_label}</p>
          )}
        </div>
      ),
    },
    {
      key: 'parent',
      label: 'Context',
      headerClassName: 'hidden lg:table-cell',
      cellClassName: 'hidden lg:table-cell',
      render: (entry) => entry.parent_entity_type ? (
        <div className="min-w-0">
          <p className="text-xs text-theme-secondary">{ENTITY_LABELS[entry.parent_entity_type] || entry.parent_entity_type}</p>
          {entry.parent_entity_label && (
            <p className="text-xs text-theme-secondary truncate max-w-36">{entry.parent_entity_label}</p>
          )}
        </div>
      ) : (
        <span className="text-xs text-theme-secondary">-</span>
      ),
    },
    {
      key: 'expand',
      label: '',
      headerClassName: 'w-8',
      render: (entry) => entry.details ? (
        <button
          onClick={(e) => { e.stopPropagation(); setExpandedId(expandedId === entry.id ? null : entry.id); }}
          className="p-1 rounded hover:bg-subtle transition-colors text-theme-secondary"
        >
          {expandedId === entry.id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
      ) : null,
    },
  ], [expandedId]);

  const buildRowTooltip = useCallback((entry: AuditLogEntry): string => {
    if (!entry.details || expandedId === entry.id) return '';
    const lines: string[] = [];
    lines.push(`${ACTION_LABELS[entry.action] || entry.action} ${ENTITY_LABELS[entry.entity_type] || entry.entity_type}`);
    if (entry.entity_label) lines.push(`Entity: ${entry.entity_label}`);
    if (entry.user_email) lines.push(`User: ${entry.user_email} (${entry.user_role})`);
    lines.push(`Time: ${formatTimestamp(entry.created_at)}`);
    if (entry.parent_entity_type) {
      const parentLabel = ENTITY_LABELS[entry.parent_entity_type] || entry.parent_entity_type;
      lines.push(`Context: ${parentLabel}${entry.parent_entity_label ? ` - ${entry.parent_entity_label}` : ''}`);
    }
    if (entry.page_context) lines.push(`Page: ${entry.page_context}`);
    lines.push('');
    lines.push('--- Details ---');
    for (const [key, value] of Object.entries(entry.details)) {
      if (value && typeof value === 'object' && 'old' in (value as Record<string, unknown>) && 'new' in (value as Record<string, unknown>)) {
        const change = value as { old: unknown; new: unknown };
        lines.push(`${key}: ${String(change.old ?? 'null')} -> ${String(change.new ?? 'null')}`);
      } else {
        lines.push(`${key}: ${typeof value === 'object' ? JSON.stringify(value) : String(value ?? '')}`);
      }
    }
    return lines.join('\n');
  }, [expandedId]);

  const renderExpandedRow = (entry: AuditLogEntry) => {
    if (!entry.details) return null;
    return (
      <div className="bg-subtle rounded-lg border border-line p-4 space-y-2">
        <h4 className="text-xs font-semibold text-theme-secondary uppercase tracking-wider">Details</h4>
        <div className="space-y-1">
          {Object.entries(entry.details).map(([key, value]) => {
            if (value && typeof value === 'object' && 'old' in (value as Record<string, unknown>) && 'new' in (value as Record<string, unknown>)) {
              const change = value as { old: unknown; new: unknown };
              return (
                <div key={key} className="flex items-start gap-2 text-sm">
                  <span className="font-medium text-theme-secondary min-w-24">{key}:</span>
                  <span className="text-red-600 line-through dark:text-red-400">{String(change.old ?? 'null')}</span>
                  <span className="text-theme-secondary mx-1">-&gt;</span>
                  <span className="text-green-700 dark:text-green-300">{String(change.new ?? 'null')}</span>
                </div>
              );
            }
            return (
              <div key={key} className="flex items-start gap-2 text-sm">
                <span className="font-medium text-theme-secondary min-w-24">{key}:</span>
                <span className="text-theme-secondary break-all">{typeof value === 'object' ? JSON.stringify(value) : String(value ?? '')}</span>
              </div>
            );
          })}
        </div>
        {entry.page_context && (
          <p className="text-xs text-theme-secondary pt-1 border-t border-line">
            Page: {entry.page_context}
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto h-full flex flex-col overflow-hidden">
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <div>
          <div className="flex items-center gap-2">
            <ClipboardList className="w-6 h-6 text-blue-600" />
            <h1 className="text-2xl font-bold text-theme-primary">Activity Log</h1>
          </div>
          <p className="text-sm text-theme-secondary mt-1">Track all write operations across the application</p>
        </div>
        <div className="flex items-center gap-2">
          <div
            className="flex items-center gap-1.5 px-2 py-2 bg-subtle rounded-lg text-sm text-theme-secondary"
            title="Automatically refresh the activity log on this interval"
          >
            <RefreshCw
              size={14}
              className={autoRefreshing ? 'animate-spin text-blue-600' : ''}
              aria-hidden
            />
            <span className="hidden sm:inline text-xs">Auto</span>
            <select
              value={autoRefreshMs}
              onChange={(e) => setAutoRefreshMs(Number(e.target.value))}
              className="bg-transparent border-0 focus:outline-none focus:ring-0 cursor-pointer text-sm font-medium"
              aria-label="Auto-refresh interval"
            >
              {AUTO_REFRESH_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <button
            onClick={() => { void fetchLogs(); void fetchStats(); }}
            className="flex items-center gap-1.5 px-3 py-2 bg-subtle text-theme-secondary hover:bg-subtle rounded-lg transition-colors text-sm"
          >
            <RefreshCw size={14} />
            Refresh
          </button>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors text-sm disabled:opacity-50"
          >
            {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            Export CSV
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-4 flex-shrink-0">
        <div className="bg-surface rounded-lg border border-line p-4">
          <p className="text-xs font-medium text-theme-secondary uppercase tracking-wider">Today</p>
          <p className="text-2xl font-bold text-theme-primary mt-1">{stats.today.toLocaleString()}</p>
          <p className="text-xs text-theme-secondary">events recorded</p>
        </div>
        <div className="bg-surface rounded-lg border border-line p-4">
          <p className="text-xs font-medium text-theme-secondary uppercase tracking-wider">This Week</p>
          <p className="text-2xl font-bold text-theme-primary mt-1">{stats.this_week.toLocaleString()}</p>
          <p className="text-xs text-theme-secondary">events recorded</p>
        </div>
        <div className="bg-surface rounded-lg border border-line p-4">
          <p className="text-xs font-medium text-theme-secondary uppercase tracking-wider">Active Users</p>
          <p className="text-2xl font-bold text-theme-primary mt-1">{stats.active_users}</p>
          <p className="text-xs text-theme-secondary">this week</p>
        </div>
      </div>

      <div className="bg-surface rounded-lg border border-line p-3 mb-4 flex-shrink-0">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1.5 bg-subtle rounded-lg p-1">
            {DATE_PRESETS.map((preset) => (
              <button
                key={preset.hours}
                onClick={() => setDatePreset(preset.hours)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  datePreset === preset.hours
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-theme-secondary hover:bg-subtle'
                }`}
              >
                {preset.label}
              </button>
            ))}
          </div>

          <select
            value={filterAction}
            onChange={(e) => setFilterAction(e.target.value)}
            className="text-sm border border-line rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-surface text-theme-primary"
          >
            <option value="">All Actions</option>
            {Object.entries(AUDIT_ACTIONS).map(([, val]) => (
              <option key={val} value={val}>{ACTION_LABELS[val] || val}</option>
            ))}
          </select>

          <select
            value={filterEntity}
            onChange={(e) => setFilterEntity(e.target.value)}
            className="text-sm border border-line rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-surface text-theme-primary"
          >
            <option value="">All Entities</option>
            {Object.entries(AUDIT_ENTITIES).map(([, val]) => (
              <option key={val} value={val}>{ENTITY_LABELS[val] || val}</option>
            ))}
          </select>

          <select
            value={filterUser}
            onChange={(e) => setFilterUser(e.target.value)}
            className="text-sm border border-line rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-surface text-theme-primary"
          >
            <option value="">All Users</option>
            {users.map((email) => (
              <option key={email} value={email}>{email}</option>
            ))}
          </select>

          <div className="relative flex-1 min-w-48">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-theme-secondary" />
            <input
              type="text"
              placeholder="Search labels or emails..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-8 py-1.5 text-sm border border-line bg-surface text-theme-primary rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {searchQuery && (
              <button
                onClick={() => { setSearchQuery(''); setDebouncedSearch(''); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-theme-secondary hover:text-theme-secondary"
              >
                <X size={14} />
              </button>
            )}
          </div>

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="text-xs text-blue-600 hover:text-blue-800 font-medium whitespace-nowrap"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      <DataTable
        data={logs}
        columns={columns}
        keyField="id"
        loading={loading}
        emptyIcon={<ClipboardList size={40} className="text-theme-secondary" />}
        emptyTitle="No activity found"
        emptyDescription="Try adjusting your date range or filters"
        sortKey={sortKey}
        sortDir={sortDir}
        onSort={handleSort}
        serverPagination
        totalCount={totalCount}
        page={page}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        onRowClick={(entry) => {
          if (entry.details) {
            setExpandedId(expandedId === entry.id ? null : entry.id);
          }
        }}
        expandedId={expandedId}
        expandedRow={renderExpandedRow}
        rowTitle={buildRowTooltip}
      />
    </div>
  );
}
